/**
 * Path Social → Outfame migration helpers.
 *
 * Shared by the gift-reveal landing page (/gift) and the /setup migration banner.
 * Param contract (carried by our own redirect/email links only):
 *   ref=ps | utm_campaign=path_social_rebrand | ps_l=…      → migration visitor (banner gate)
 *   plan=core|elite|expert                                    → their Path Social plan tier
 *   price=…                                                   → optional explicit price string override
 *   username / email / coupon                                 → prefills /setup already reads
 */

export type PsPlanSlug = "core" | "elite" | "expert"

export interface PsPlanMapping {
    /** Display name of their Path Social plan (banner continuity copy). */
    psName: string
    /** The Outfame plan their PS tier maps to. */
    outfamePlan: "Go" | "Grow" | "Glory"
    /** Outfame yearly per-month price for the mapped plan ($/mo billed yearly), as shown on /pricing. */
    yearlyPrice: number
    /** Their Path Social per-month price when billed annually (banner price-honoring copy). Null = unknown. */
    psYearlyPrice: number | null
}

const PLAN_MAP: Record<string, PsPlanMapping> = {
    core: { psName: "Core", outfamePlan: "Go", yearlyPrice: 29, psYearlyPrice: 29 },
    elite: { psName: "Elite", outfamePlan: "Grow", yearlyPrice: 41, psYearlyPrice: 41 },
    expert: { psName: "Expert", outfamePlan: "Grow", yearlyPrice: 41, psYearlyPrice: null }
}

/** Normalize any plan param spelling (core, Core, core-annual, go…) to a mapping. Unknown → Go. */
export function mapPsPlan(planParam: string | null | undefined): PsPlanMapping {
    if (!planParam) return PLAN_MAP.core
    const slug = planParam.trim().toLowerCase().split("-")[0]
    return PLAN_MAP[slug] ?? PLAN_MAP.core
}

/** Is this visitor arriving from the Path Social migration flow? (banner gate) */
export function isMigrationVisit(params: {
    get: (key: string) => string | null
    has?: (key: string) => boolean
}): boolean {
    if (params.get("utm_campaign") === "path_social_rebrand") return true
    if (params.get("ref") === "ps") return true
    if (params.has?.("ps_l")) return true
    return false
}
